import React, { useState, useEffect } from 'react';
import SEO from '../../components/SEO';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import Image from '../../components/AppImage';
import Button from '../../components/ui/Button';
import LeadFormButton from '../../components/LeadFormButton';
import { useLeadForm } from '../../contexts/LeadFormContext';
import ProjectCard from './components/ProjectCard';
import ProjectFilters from './components/ProjectFilters';
import ProjectModal from './components/ProjectModal';
import BeforeAfterSlider from './components/BeforeAfterSlider';
import ClientTestimonials from './components/ClientTestimonials';
import ProjectStats from './components/ProjectStats';
import { PRODUCT_CATEGORIES, CATEGORY_LABELS } from '../../constants/productsData';

const ProjectGallery = () => {
  const { openLeadForm } = useLeadForm();
  const [filters, setFilters] = useState({
    category: 'all',
    style: 'all',
    budget: 'all'
  });
  const [selectedProject, setSelectedProject] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [filteredProjects, setFilteredProjects] = useState([]);

  // Mock data for projects
  const projects = [
    {
      id: 1,
      title: "Residência Jardim Europa",
      description: "Transformação completa de uma residência de alto padrão com móveis sob medida em madeira nobre.",
      fullDescription: `Este projeto representa uma das nossas realizações mais emblemáticas, onde transformamos completamente os ambientes de uma residência de 400m² no Jardim Europa. Utilizamos madeira de freijó e mogno para criar peças únicas que dialogam perfeitamente com a arquitetura clássica da casa.\n\nCada ambiente foi pensado para refletir a personalidade sofisticada dos proprietários, combinando funcionalidade e elegância. O projeto incluiu sala de estar, jantar, home office, suíte master e área gourmet.`,
      mainImage: "https://images.unsplash.com/photo-1586023492125-27b2c045efd7?w=800&h=600&fit=crop",
      gallery: [
        "https://images.unsplash.com/photo-1586023492125-27b2c045efd7?w=800&h=600&fit=crop",
        "https://images.unsplash.com/photo-1567538096630-e0c55bd6374c?w=800&h=600&fit=crop",
        "https://images.unsplash.com/photo-1586023492125-27b2c045efd7?w=800&h=600&fit=crop",
        "https://images.unsplash.com/photo-1567538096630-e0c55bd6374c?w=800&h=600&fit=crop"
      ],
      category: "Residencial",
      budgetRange: "Premium",
      location: "Jardim Europa, SP",
      completionDate: "Mar 2024",
      duration: "4 meses",
      pieceCount: 25,
      rating: 5,
      client: {
        name: "Maria Helena Santos",
        title: "Empresária",
        avatar: "https://randomuser.me/api/portraits/women/45.jpg"
      },
      testimonial: "A PH Móveis superou todas as nossas expectativas. Cada peça foi pensada nos mínimos detalhes e a qualidade é excepcional. Nossa casa se tornou ainda mais acolhedora e elegante.",
      videoTestimonial: null,
      videoThumbnail: "https://images.unsplash.com/photo-1586023492125-27b2c045efd7?w=400&h=300&fit=crop",
      timeline: [
        { phase: "Consulta e Projeto", date: "Nov 2023" },
        { phase: "Aprovação e Produção", date: "Dez 2023" },
        { phase: "Instalação", date: "Fev 2024" },
        { phase: "Entrega Final", date: "Mar 2024" }
      ],
      relatedProducts: [
        {
          name: "Mesa de Jantar Freijó",
          category: "Sala de Jantar",
          image: "https://images.unsplash.com/photo-1549497538-303791108f95?w=200&h=150&fit=crop"
        },
        {
          name: "Estante Modular",
          category: "Sala de Estar",
          image: "https://images.unsplash.com/photo-1586023492125-27b2c045efd7?w=200&h=150&fit=crop"
        }
      ],
      categoryId: 0, // sofas
      style: 'classic',
      budget: 'premium'
    },
    {
      id: 2,
      title: "Apartamento Vila Madalena",
      description: "Design contemporâneo para jovem casal com soluções inteligentes de armazenamento.",
      fullDescription: `Projeto desenvolvido para um jovem casal de arquitetos que buscava móveis funcionais e com design contemporâneo. O desafio era otimizar o espaço de 80m² sem abrir mão do estilo.\n\nUtilizamos madeira de carvalho americano combinada com detalhes em metal preto fosco, criando um ambiente moderno e aconchegante. Todas as peças foram projetadas com soluções inteligentes de armazenamento.`,
      mainImage: "https://images.unsplash.com/photo-1567538096630-e0c55bd6374c?w=800&h=600&fit=crop",
      gallery: [
        "https://images.unsplash.com/photo-1567538096630-e0c55bd6374c?w=800&h=600&fit=crop",
        "https://images.unsplash.com/photo-1586023492125-27b2c045efd7?w=800&h=600&fit=crop",
        "https://images.unsplash.com/photo-1567538096630-e0c55bd6374c?w=800&h=600&fit=crop"
      ],
      category: "Apartamento",
      budgetRange: "Luxo",
      location: "Vila Madalena, SP",
      completionDate: "Jan 2024",
      duration: "3 meses",
      pieceCount: 18,
      rating: 5,
      client: {
        name: "Carlos e Ana Ribeiro",
        title: "Arquitetos",
        avatar: "https://randomuser.me/api/portraits/men/32.jpg"
      },
      testimonial: "Como arquitetos, somos muito exigentes com qualidade e design. A PH Móveis nos impressionou com a atenção aos detalhes e a capacidade de transformar nossas ideias em realidade.",
      videoTestimonial: null,
      videoThumbnail: "https://images.unsplash.com/photo-1567538096630-e0c55bd6374c?w=400&h=300&fit=crop",
      timeline: [
        { phase: "Briefing Inicial", date: "Out 2023" },
        { phase: "Desenvolvimento", date: "Nov 2023" },
        { phase: "Produção", date: "Dez 2023" },
        { phase: "Instalação", date: "Jan 2024" }
      ],
      relatedProducts: [
        {
          name: "Rack TV Suspenso",
          category: "Sala de Estar",
          image: "https://images.unsplash.com/photo-1586023492125-27b2c045efd7?w=200&h=150&fit=crop"
        },
        {
          name: "Mesa de Centro",
          category: "Sala de Estar",
          image: "https://images.unsplash.com/photo-1567538096630-e0c55bd6374c?w=200&h=150&fit=crop"
        }
      ],
      categoryId: 0, // sofas
      style: 'contemporary',
      budget: 'luxury'
    },
    {
      id: 3,
      title: "Cobertura Itaim Bibi",
      description: "Móveis minimalistas para cobertura com vista panorâmica da cidade.",
      fullDescription: `Esta cobertura de 300m² no Itaim Bibi demandava móveis que não competissem com a vista espetacular da cidade. Optamos por linhas minimalistas e madeira clara de maple canadense.\n\nO projeto focou em criar ambientes integrados que valorizassem a amplitude dos espaços e a entrada de luz natural. Cada peça foi posicionada estrategicamente para não obstruir as visuais externas.`,
      mainImage: "https://images.unsplash.com/photo-1549497538-303791108f95?w=800&h=600&fit=crop",
      gallery: [
        "https://images.unsplash.com/photo-1549497538-303791108f95?w=800&h=600&fit=crop",
        "https://images.unsplash.com/photo-1586023492125-27b2c045efd7?w=800&h=600&fit=crop",
        "https://images.unsplash.com/photo-1567538096630-e0c55bd6374c?w=800&h=600&fit=crop"
      ],
      category: "Cobertura",
      budgetRange: "Premium",
      location: "Itaim Bibi, SP",
      completionDate: "Fev 2024",
      duration: "5 meses",
      pieceCount: 22,
      rating: 5,
      client: {
        name: "Roberto Mendes",
        title: "CEO Tech Startup",
        avatar: "https://randomuser.me/api/portraits/men/28.jpg"
      },
      testimonial: "Precisava de móveis que complementassem a arquitetura moderna da cobertura. O resultado foi perfeito - elegante, funcional e com a qualidade que eu esperava.",
      videoTestimonial: null,
      videoThumbnail: "https://images.unsplash.com/photo-1549497538-303791108f95?w=400&h=300&fit=crop",
      timeline: [
        { phase: "Visita Técnica", date: "Set 2023" },
        { phase: "Projeto Executivo", date: "Out 2023" },
        { phase: "Fabricação", date: "Nov 2023 - Jan 2024" },
        { phase: "Montagem", date: "Fev 2024" }
      ],
      relatedProducts: [
        {
          name: "Buffet Minimalista",
          category: "Sala de Jantar",
          image: "https://images.unsplash.com/photo-1549497538-303791108f95?w=200&h=150&fit=crop"
        },
        {
          name: "Mesa Lateral",
          category: "Sala de Estar",
          image: "https://images.unsplash.com/photo-1586023492125-27b2c045efd7?w=200&h=150&fit=crop"
        }
      ],
      categoryId: 1, // mesas
      style: 'minimalist',
      budget: 'premium'
    },
    {
      id: 4,
      title: "Casa de Campo Campos do Jordão",
      description: "Móveis rústicos em madeira de demolição para casa de campo aconchegante.",
      fullDescription: `Esta casa de campo em Campos do Jordão pediu móveis que remetessem ao aconchego e à tradição. Utilizamos madeira de demolição de casarões antigos, preservando a história e criando peças únicas.\n\nO projeto valorizou a rusticidade natural da madeira, combinada com técnicas artesanais tradicionais. Cada peça conta uma história e contribui para a atmosfera acolhedora da casa.`,
      mainImage: "https://images.pexels.com/photos/1571460/pexels-photo-1571460.jpeg?w=800&h=600&fit=crop",
      gallery: [
        "https://images.pexels.com/photos/1571460/pexels-photo-1571460.jpeg?w=800&h=600&fit=crop",
        "https://images.pexels.com/photos/1571453/pexels-photo-1571453.jpeg?w=800&h=600&fit=crop",
        "https://images.pexels.com/photos/1571468/pexels-photo-1571468.jpeg?w=800&h=600&fit=crop"
      ],
      category: "Casa de Campo",
      budgetRange: "Padrão",
      location: "Campos do Jordão, SP",
      completionDate: "Dez 2023",
      duration: "3 meses",
      pieceCount: 15,
      rating: 5,
      client: {
        name: "Família Oliveira",
        title: "Proprietários",
        avatar: "https://randomuser.me/api/portraits/women/52.jpg"
      },
      testimonial: "Queríamos móveis que trouxessem o charme do interior para nossa casa de campo. A PH Móveis captou perfeitamente nossa visão e entregou peças lindas e funcionais.",
      videoTestimonial: null,
      videoThumbnail: "https://images.pexels.com/photos/1571460/pexels-photo-1571460.jpeg?w=400&h=300&fit=crop",
      timeline: [
        { phase: "Seleção de Madeiras", date: "Set 2023" },
        { phase: "Design e Aprovação", date: "Out 2023" },
        { phase: "Artesanato", date: "Nov 2023" },
        { phase: "Entrega", date: "Dez 2023" }
      ],
      relatedProducts: [
        {
          name: "Mesa Rústica",
          category: "Sala de Jantar",
          image: "https://images.pexels.com/photos/1571460/pexels-photo-1571460.jpeg?w=200&h=150&fit=crop"
        },
        {
          name: "Banco de Madeira",
          category: "Área Externa",
          image: "https://images.pexels.com/photos/1571453/pexels-photo-1571453.jpeg?w=200&h=150&fit=crop"
        }
      ],
      categoryId: 1, // mesas
      style: 'rustic',
      budget: 'standard'
    },
    {
      id: 5,
      title: "Escritório Corporativo Faria Lima",
      description: "Móveis executivos para escritório de advocacia com design sofisticado.",
      fullDescription: `Este projeto para um renomado escritório de advocacia na Faria Lima exigiu móveis que transmitissem seriedade e sofisticação. Utilizamos madeira de jacarandá e detalhes em couro legítimo.\n\nO ambiente precisava ser funcional para o dia a dia de trabalho, mas também impressionar clientes e parceiros. Cada peça foi projetada para durar décadas mantendo sua elegância.`,
      mainImage: "https://images.pixabay.com/photo-2016/11/30/08/46/living-room-1872192_1280.jpg?w=800&h=600&fit=crop",
      gallery: [
        "https://images.pixabay.com/photo-2016/11/30/08/46/living-room-1872192_1280.jpg?w=800&h=600&fit=crop",
        "https://images.pixabay.com/photo-2017/03/28/12/11/chairs-2181947_1280.jpg?w=800&h=600&fit=crop",
        "https://images.pixabay.com/photo-2016/11/29/12/30/office-1869579_1280.jpg?w=800&h=600&fit=crop"
      ],
      category: "Corporativo",
      budgetRange: "Luxo",
      location: "Faria Lima, SP",
      completionDate: "Nov 2023",
      duration: "4 meses",
      pieceCount: 35,
      rating: 5,
      client: {
        name: "Dr. Eduardo Silva",
        title: "Sócio-Fundador",
        avatar: "https://randomuser.me/api/portraits/men/45.jpg"
      },
      testimonial: "Nosso escritório precisava transmitir credibilidade e sofisticação. A PH Móveis entendeu perfeitamente nossas necessidades e criou um ambiente excepcional.",
      videoTestimonial: null,
      videoThumbnail: "https://images.pixabay.com/photo-2016/11/30/08/46/living-room-1872192_1280.jpg?w=400&h=300&fit=crop",
      timeline: [
        { phase: "Briefing Corporativo", date: "Jul 2023" },
        { phase: "Projeto Executivo", date: "Ago 2023" },
        { phase: "Produção", date: "Set-Out 2023" },
        { phase: "Instalação", date: "Nov 2023" }
      ],
      relatedProducts: [
        {
          name: "Mesa Executiva",
          category: "Escritório",
          image: "https://images.pixabay.com/photo-2016/11/29/12/30/office-1869579_1280.jpg?w=200&h=150&fit=crop"
        },
        {
          name: "Estante Corporativa",
          category: "Escritório",
          image: "https://images.pixabay.com/photo-2017/03/28/12/11/chairs-2181947_1280.jpg?w=200&h=150&fit=crop"
        }
      ],
      categoryId: 2, // cadeiras
      style: 'classic',
      budget: 'luxury'
    },
    {
      id: 6,
      title: "Loft Industrial Pinheiros",
      description: "Móveis com design industrial para loft moderno com estrutura aparente.",
      fullDescription: `Este loft em Pinheiros mantém as características industriais originais do prédio, com vigas e tubulações aparentes. Criamos móveis que dialogam com essa estética, usando madeira de reflorestamento e metal corten.\n\nO desafio foi criar peças que fossem robustas como a arquitetura, mas também aconchegantes para o uso residencial. O resultado é um ambiente único que preserva a personalidade industrial.`,
      mainImage: "https://images.unsplash.com/photo-1586023492125-27b2c045efd7?w=800&h=600&fit=crop",
      gallery: [
        "https://images.unsplash.com/photo-1586023492125-27b2c045efd7?w=800&h=600&fit=crop",
        "https://images.unsplash.com/photo-1567538096630-e0c55bd6374c?w=800&h=600&fit=crop",
        "https://images.unsplash.com/photo-1549497538-303791108f95?w=800&h=600&fit=crop"
      ],
      category: "Loft",
      budgetRange: "Padrão",
      location: "Pinheiros, SP",
      completionDate: "Out 2023",
      duration: "2 meses",
      pieceCount: 12,
      rating: 4,
      client: {
        name: "Lucas Ferreira",
        title: "Designer Gráfico",
        avatar: "https://randomuser.me/api/portraits/men/29.jpg"
      },
      testimonial: "Meu loft tinha uma pegada industrial forte e eu precisava de móveis que complementassem isso. A PH Móveis criou peças perfeitas que mantêm o estilo mas trazem conforto.",
      videoTestimonial: null,
      videoThumbnail: "https://images.unsplash.com/photo-1586023492125-27b2c045efd7?w=400&h=300&fit=crop",
      timeline: [
        { phase: "Análise do Espaço", date: "Ago 2023" },
        { phase: "Desenvolvimento", date: "Set 2023" },
        { phase: "Fabricação", date: "Set 2023" },
        { phase: "Montagem", date: "Out 2023" }
      ],
      relatedProducts: [
        {
          name: "Mesa Industrial",
          category: "Sala de Jantar",
          image: "https://images.unsplash.com/photo-1586023492125-27b2c045efd7?w=200&h=150&fit=crop"
        },
        {
          name: "Estante Metal e Madeira",
          category: "Sala de Estar",
          image: "https://images.unsplash.com/photo-1567538096630-e0c55bd6374c?w=200&h=150&fit=crop"
        }
      ],
      categoryId: 0, // sofas
      style: 'modern',
      budget: 'standard'
    }
  ];

  // Mock data for testimonials
  const testimonials = [
    {
      name: "Maria Helena Santos",
      title: "Empresária",
      location: "Jardim Europa, SP",
      avatar: "https://randomuser.me/api/portraits/women/45.jpg",
      rating: 5,
      testimonial: "A PH Móveis transformou completamente nossa casa. Cada peça foi pensada nos mínimos detalhes e a qualidade é excepcional. Nossa família se sente ainda mais acolhida em casa. O atendimento foi impecável do início ao fim, sempre com muita atenção às nossas necessidades específicas.",
      homeImage: "https://images.unsplash.com/photo-1586023492125-27b2c045efd7?w=600&h=400&fit=crop",
      additionalImages: [
        "https://images.unsplash.com/photo-1567538096630-e0c55bd6374c?w=200&h=150&fit=crop",
        "https://images.unsplash.com/photo-1549497538-303791108f95?w=200&h=150&fit=crop",
        "https://images.pexels.com/photos/1571460/pexels-photo-1571460.jpeg?w=200&h=150&fit=crop"
      ],
      videoUrl: null,
      projectType: "Residência Completa",
      investment: "R$ 85.000",
      duration: "4 meses"
    },
    {
      name: "Carlos e Ana Ribeiro",
      title: "Arquitetos",
      location: "Vila Madalena, SP",
      avatar: "https://randomuser.me/api/portraits/men/32.jpg",
      rating: 5,
      testimonial: "Como arquitetos, somos muito exigentes com qualidade e design. A PH Móveis nos impressionou com a capacidade técnica e criativa. Conseguiram transformar nossas ideias em realidade, superando nossas expectativas. A execução foi perfeita e o prazo foi cumprido rigorosamente.",
      homeImage: "https://images.unsplash.com/photo-1567538096630-e0c55bd6374c?w=600&h=400&fit=crop",
      additionalImages: [
        "https://images.unsplash.com/photo-1586023492125-27b2c045efd7?w=200&h=150&fit=crop",
        "https://images.unsplash.com/photo-1549497538-303791108f95?w=200&h=150&fit=crop",
        "https://images.pexels.com/photos/1571453/pexels-photo-1571453.jpeg?w=200&h=150&fit=crop"
      ],
      videoUrl: null,
      projectType: "Apartamento Moderno",
      investment: "R$ 45.000",
      duration: "3 meses"
    },
    {
      name: "Roberto Mendes",
      title: "CEO Tech Startup",
      location: "Itaim Bibi, SP",
      avatar: "https://randomuser.me/api/portraits/men/28.jpg",
      rating: 5,
      testimonial: "Minha cobertura tem uma vista incrível da cidade e eu precisava de móveis que complementassem, não competissem com ela. A PH Móveis entendeu perfeitamente minha visão. O resultado é um ambiente elegante, funcional e que valoriza ainda mais o espaço. Recomendo sem hesitar.",
      homeImage: "https://images.unsplash.com/photo-1549497538-303791108f95?w=600&h=400&fit=crop",
      additionalImages: [
        "https://images.unsplash.com/photo-1586023492125-27b2c045efd7?w=200&h=150&fit=crop",
        "https://images.unsplash.com/photo-1567538096630-e0c55bd6374c?w=200&h=150&fit=crop",
        "https://images.pixabay.com/photo-2016/11/30/08/46/living-room-1872192_1280.jpg?w=200&h=150&fit=crop"
      ],
      videoUrl: null,
      projectType: "Cobertura Minimalista",
      investment: "R$ 120.000",
      duration: "5 meses"
    }
  ];

  // Mock data for stats
  const stats = {
    totalProjects: "150+",
    happyClients: "98%",
    averageRating: "4.9",
    awards: "12",
    averageDuration: "3.5",
    repeatClients: "85"
  };

  // Filter projects based on current filters
  useEffect(() => {
    let filtered = projects;

    if (filters?.category !== 'all') {
      filtered = filtered?.filter(project => project?.categoryId === parseInt(filters?.category));
    }

    if (filters?.style !== 'all') {
      filtered = filtered?.filter(project => project?.style === filters?.style);
    }

    if (filters?.budget !== 'all') {
      filtered = filtered?.filter(project => project?.budget === filters?.budget);
    }

    setFilteredProjects(filtered);
  }, [filters]);

  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
  };

  const handleClearFilters = () => {
    setFilters({
      room: 'all',
      style: 'all',
      budget: 'all'
    });
  };

  const getActiveFiltersCount = () => {
    return Object.values(filters)?.filter(value => value !== 'all')?.length;
  };

  const handleViewDetails = (project) => {
    setSelectedProject(project);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedProject(null);
  };

  const handleShareProject = (project) => {
    if (navigator.share) {
      navigator.share({
        title: project?.title,
        text: project?.description,
        url: window.location?.href
      });
    } else {
      // Fallback for browsers that don't support Web Share API
      const shareText = `Confira este projeto incrível da PH Móveis: ${project?.title} - ${project?.description}`;
      navigator.clipboard?.writeText(shareText);
      // You could show a toast notification here
    }
  };

  // handleWhatsAppContact removido - usando contexto de lead form

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "ImageGallery",
    "name": "Galeria de Projetos - PH Móveis Artesanais",
    "description": "Mais de 150 projetos realizados com 98% de satisfação. Veja transformações reais e inspire-se.",
    "url": "https://phmoveis.com.br/project-gallery",
    "image": "https://phmoveis.com.br/assets/gallery-og.jpg",
    "numberOfItems": projects.length,
    "mainEntity": {
      "@type": "ItemList",
      "name": "Projetos Realizados",
      "numberOfItems": projects.length,
      "itemListElement": projects.map((project, index) => ({
        "@type": "CreativeWork",
        "position": index + 1,
        "name": project.title,
        "description": project.description,
        "image": project.mainImage,
        "creator": {
          "@type": "Organization",
          "name": "PH Móveis Artesanais"
        },
        "dateCreated": project.completionDate,
        "locationCreated": {
          "@type": "Place",
          "name": project.location
        }
      }))
    }
  };

  return (
    <>
      <SEO
        title="Galeria de Projetos | Inspiração em Móveis Sob Medida"
        description="Explore nossa galeria com mais de 150 projetos realizados. Veja transformações reais, depoimentos de clientes e inspire-se para seu próximo projeto de móveis sob medida."
        keywords="galeria projetos móveis, antes e depois móveis, depoimentos clientes, projetos residenciais, móveis sob medida São Paulo, transformações, inspiração, design de interiores"
        image="https://phmoveis.com.br/assets/gallery-og.jpg"
        url="https://phmoveis.com.br/project-gallery"
        type="website"
        structuredData={structuredData}
      />
      <div className="min-h-screen bg-background">
        <Header />
        
        {/* Hero Section */}
        <section className="pt-20 lg:pt-24 pb-12 bg-gradient-forest text-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-4xl mx-auto">
              <div className="flex items-center justify-center space-x-2 mb-4">
                <Icon name="Image" size={24} color="white" />
                <span className="text-accent font-medium">Galeria de Projetos</span>
              </div>
              <h1 className="font-playfair font-bold text-4xl lg:text-6xl mb-6">
                Transformações que
                <span className="text-accent block">Inspiram</span>
              </h1>
              <p className="text-xl text-white/90 mb-8 leading-relaxed">
                Descubra mais de 150 projetos realizados com excelência. Cada peça conta uma história única de artesanato, qualidade e satisfação do cliente.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-6">
                <LeadFormButton
                  variant="secondary"
                  size="lg"
                  iconName="MessageCircle"
                  iconPosition="left"
                  className="bg-accent text-accent-foreground hover:bg-accent/90"
                >
                  Conversar sobre Meu Projeto
                </LeadFormButton>
              </div>
            </div>
          </div>
        </section>

        {/* Stats Section */}
        <section className="py-12 bg-muted">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <ProjectStats stats={stats} />
          </div>
        </section>

        {/* Main Content */}
        <section className="py-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-4 gap-8">
              {/* Filters Sidebar */}
              <div className="lg:col-span-1">
                <div className="sticky top-24">
                  <ProjectFilters
                    filters={filters}
                    onFilterChange={handleFilterChange}
                    onClearFilters={handleClearFilters}
                    activeFiltersCount={getActiveFiltersCount()}
                  />
                </div>
              </div>

              {/* Projects Grid */}
              <div className="lg:col-span-3">
                {/* Results Header */}
                <div className="flex items-center justify-between mb-8">
                  <div>
                    <h2 className="font-playfair font-bold text-2xl text-text-primary">
                      Nossos Projetos
                    </h2>
                    <p className="text-text-secondary">
                      {filteredProjects?.length} projeto{filteredProjects?.length !== 1 ? 's' : ''} encontrado{filteredProjects?.length !== 1 ? 's' : ''}
                    </p>
                  </div>
                  <div className="flex items-center space-x-2 text-sm text-text-secondary">
                    <Icon name="Filter" size={16} />
                    <span>Filtros ativos: {getActiveFiltersCount()}</span>
                  </div>
                </div>

                {/* Projects Grid */}
                {filteredProjects?.length > 0 ? (
                  <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-6">
                    {filteredProjects?.map((project) => (
                      <ProjectCard
                        key={project?.id}
                        project={project}
                        onViewDetails={handleViewDetails}
                        onShare={handleShareProject}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <Icon name="Search" size={48} color="var(--color-text-secondary)" className="mx-auto mb-4" />
                    <h3 className="font-semibold text-lg text-text-primary mb-2">
                      Nenhum projeto encontrado
                    </h3>
                    <p className="text-text-secondary mb-6">
                      Tente ajustar os filtros para ver mais resultados.
                    </p>
                    <Button
                      variant="outline"
                      iconName="RotateCcw"
                      iconPosition="left"
                      onClick={handleClearFilters}
                    >
                      Limpar Filtros
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* Before/After Section */}
        <section className="py-12 bg-muted">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="font-playfair font-bold text-3xl text-text-primary mb-4">
                Antes & Depois
              </h2>
              <p className="text-text-secondary max-w-2xl mx-auto">
                Veja as transformações incríveis que realizamos. Cada projeto é uma jornada única de renovação e personalização.
              </p>
            </div>

            <div className="grid lg:grid-cols-2 gap-8">
              <BeforeAfterSlider
                title="Sala de Estar - Jardim Europa"
                beforeImage="https://images.unsplash.com/photo-1586023492125-27b2c045efd7?w=600&h=400&fit=crop"
                afterImage="https://images.unsplash.com/photo-1567538096630-e0c55bd6374c?w=600&h=400&fit=crop"
              />
              <BeforeAfterSlider
                title="Escritório Corporativo - Faria Lima"
                beforeImage="https://images.pixabay.com/photo-2016/11/29/12/30/office-1869579_1280.jpg?w=600&h=400&fit=crop"
                afterImage="https://images.pixabay.com/photo-2016/11/30/08/46/living-room-1872192_1280.jpg?w=600&h=400&fit=crop"
              />
            </div>
          </div>
        </section>

        {/* Client Testimonials */}
        <section className="py-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="font-playfair font-bold text-3xl text-text-primary mb-4">
                O Que Nossos Clientes Dizem
              </h2>
              <p className="text-text-secondary max-w-2xl mx-auto">
                Histórias reais de transformação e satisfação. Cada depoimento representa nossa dedicação à excelência.
              </p>
            </div>

            <ClientTestimonials testimonials={testimonials} />
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 bg-gradient-forest text-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <Icon name="Sparkles" size={48} color="var(--color-accent)" className="mx-auto mb-6" />
            <h2 className="font-playfair font-bold text-3xl lg:text-4xl mb-6">
              Pronto para Criar Seu Projeto dos Sonhos?
            </h2>
            <p className="text-xl text-white/90 mb-8">
              Junte-se aos mais de 150 clientes satisfeitos. Vamos transformar sua visão em realidade com móveis únicos e de qualidade excepcional.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-6">
              <LeadFormButton
                variant="secondary"
                size="lg"
                iconName="MessageCircle"
                iconPosition="left"
                className="bg-accent text-accent-foreground hover:bg-accent/90"
              >
                Iniciar Meu Projeto
              </LeadFormButton>
              <LeadFormButton
                variant="ghost"
                size="lg"
                iconName="Phone"
                iconPosition="left"
                className="text-white border-white hover:bg-white/10"
              >
                (62) 99326-3202
              </LeadFormButton>
            </div>
          </div>
        </section>

        {/* Project Modal */}
        <ProjectModal
          project={selectedProject}
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          onShare={handleShareProject}
        />

        {/* Footer */}
        <footer className="bg-text-primary text-white py-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid md:grid-cols-4 gap-8">
              <div className="md:col-span-2">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="w-10 h-10 bg-gradient-forest rounded-lg flex items-center justify-center">
                    <Icon name="TreePine" size={24} color="white" />
                  </div>
                  <div>
                    <span className="font-playfair font-bold text-xl">PH Móveis</span>
                    <span className="block text-sm text-white/70">Artesanais</span>
                  </div>
                </div>
                <p className="text-white/80 mb-4">
                  Criamos móveis únicos que contam sua história. Cada peça é uma obra de arte funcional, feita com paixão e dedicação aos detalhes.
                </p>
                <div className="flex items-center space-x-4">
                  <Icon name="MapPin" size={16} color="var(--color-accent)" />
                  <span className="text-white/80">São Paulo, SP</span>
                </div>
              </div>
              
              <div>
                <h4 className="font-semibold mb-4">Navegação</h4>
                <ul className="space-y-2 text-white/80">
                  <li><a href="/homepage" className="hover:text-accent transition-colors">Início</a></li>
                  <li><a href="/interactive-catalog" className="hover:text-accent transition-colors">Catálogo</a></li>
                  <li><a href="/about-us" className="hover:text-accent transition-colors">Sobre Nós</a></li>
                  <li><a href="/contact-consultation" className="hover:text-accent transition-colors">Contato</a></li>
                </ul>
              </div>
              
              <div>
                <h4 className="font-semibold mb-4">Contato</h4>
                <div className="space-y-2 text-white/80">
                  <div className="flex items-center space-x-2">
                    <Icon name="Phone" size={14} />
                    <span>(62) 99326-3202</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Icon name="Mail" size={14} />
                    <span>phmoveisconteporaneos@gmail.com</span>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="border-t border-white/20 mt-8 pt-8 text-center text-white/60">
              <p>&copy; {new Date()?.getFullYear()} PH Móveis Artesanais. Todos os direitos reservados.</p>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
};

export default ProjectGallery;